import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { printService } from 'api/services/printService';
import type { PrintAgent, PrintAgentQuery, PrintAgentStatus } from 'api/types';
import PrintReasonModal from 'components/modals/modules/print-module/PrintReasonModal';
import PrintAgentEditModal from 'components/modals/modules/print-module/PrintAgentEditModal';
import PairPrintAgentModal from 'components/modals/modules/print-module/PairPrintAgentModal';
import PairingCodesModal from 'components/modals/modules/print-module/PairingCodesModal';
import { AgentStatusChip, AGENT_STATUS_LABEL, OnlineDot, fmtAgo, fmtTime, apiMessage } from './printUi';

/**
 * Print Agents (KOT printing, architecture section 15.1): the small program on
 * a PC at each site that prints the jobs. Pair one with a one-time code, then
 * approve, suspend, resume or revoke it. Revoke is final: its key is refused
 * from then on, and a new machine is paired with "Replace agent".
 *
 * Paged, searched and filtered on the server.
 */

type Action = 'approve' | 'resume' | 'suspend' | 'revoke';

const PrintAgentsScreen: React.FC<{ allowedActions?: string[] }> = ({ allowedActions = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [draftStatus, setDraftStatus] = useState<string>('');
  const [query, setQuery] = useState<PrintAgentQuery>({ page: 1, pageSize: 25 });
  const [rows, setRows] = useState<PrintAgent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [openCodes, setOpenCodes] = useState(0);

  const [pairing, setPairing] = useState<{ replace: PrintAgent | null } | null>(null);
  const [codesOpen, setCodesOpen] = useState(false);
  const [editing, setEditing] = useState<PrintAgent | null>(null);
  const [acting, setActing] = useState<{ agent: PrintAgent; action: Action } | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const canAdd = allowedActions.includes('add') || allowedActions.includes('create');
  const canEdit = allowedActions.includes('edit') || allowedActions.includes('update');
  const canManage = allowedActions.includes('manage') || allowedActions.includes('update');
  const canWithdraw = allowedActions.includes('delete') || allowedActions.includes('manage');

  useEffect(() => {
    const t = setTimeout(() => setQuery((q) => ({ ...q, search: searchQuery.trim() || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);
  useEffect(() => { setQuery((q) => ({ ...q, page: 1, status: (statusFilter || undefined) as PrintAgentStatus | undefined })); }, [statusFilter]);

  const loadCodes = useCallback(async () => {
    try { setOpenCodes((await printService.getPairingCodes()).length); } catch { setOpenCodes(0); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await printService.getAgents(query);
      setRows(r.data);
      setTotal(r.pagination.totalItems);
    } catch (err) {
      setLoadError(apiMessage(err, 'The Print Agents could not be loaded.'));
      setRows([]); setTotal(0);
    } finally { setLoading(false); }
  }, [query]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { void loadCodes(); }, [loadCodes]);

  const refresh = useCallback(() => { void load(); void loadCodes(); }, [load, loadCodes]);

  const handleExport = useCallback(() => {
    if (rows.length === 0) { notificationService.toast.warning('No data to export'); return; }
    const headers = ['Agent', 'Site', 'Status', 'Reason', 'Online', 'Last seen', 'Key storage', 'Machine', 'OS', 'Version', 'Printers', 'Printers offline'];
    const lines = rows.map((r) => [r.agentName, r.siteName ?? '', AGENT_STATUS_LABEL[r.agentStatus] ?? r.agentStatus, r.statusReason ?? '', r.online ? 'Yes' : 'No',
      r.lastSeenAt ?? '', r.keyStorage, r.machineName ?? '', r.osDescription ?? '', r.agentVersion ?? '', r.printerCount, r.offlinePrinterCount]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const url = URL.createObjectURL(new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `print-agents-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  const runAction = useCallback(async (reason: string) => {
    if (!acting) return;
    const { agent, action } = acting;
    setBusy(true); setActionError(null);
    try {
      if (action === 'approve') await printService.approveAgent(agent.id);
      else if (action === 'resume') await printService.resumeAgent(agent.id);
      else if (action === 'suspend') await printService.suspendAgent(agent.id, reason);
      else await printService.revokeAgent(agent.id, reason);
      const done = { approve: 'approved: it can print now', resume: 'resumed', suspend: 'suspended: it gets no jobs until resumed', revoke: 'revoked' }[action];
      notificationService.toast.success(`${agent.agentName} ${done}.`);
      setActing(null);
      void load();
    } catch (err) {
      setActionError(apiMessage(err, 'That could not be done.'));
    } finally { setBusy(false); }
  }, [acting, load]);

  const columns = useMemo((): SyutGridColumn<PrintAgent>[] => [
    { id: 'agentName', header: 'Agent', accessorKey: 'agentName', enableSorting: true, size: 190,
      cell: ({ row }) => (
        <span>{row.original.agentName}
          {row.original.replacedByAgentName && <span className="prn-sub">replaced by {row.original.replacedByAgentName}</span>}
        </span>
      ) },
    { id: 'siteName', header: 'Site', accessorKey: 'siteName', enableSorting: true, size: 150, cell: ({ row }) => row.original.siteName ?? '—' },
    { id: 'agentStatus', header: 'Status', accessorKey: 'agentStatus', enableSorting: true, size: 170,
      cell: ({ row }) => (
        <span data-agent-status={row.original.agentStatus}>
          <AgentStatusChip status={row.original.agentStatus} reason={row.original.statusReason} />
          {row.original.statusReason && row.original.agentStatus !== 'ACTIVE' && <span className="prn-sub" title={row.original.statusReason}>{row.original.statusReason}</span>}
        </span>
      ) },
    { id: 'online', header: 'Online', accessorKey: 'lastSeenAt', enableSorting: true, size: 150,
      cell: ({ row }) => (
        <span title={row.original.lastSeenAt ? `Last seen ${fmtTime(row.original.lastSeenAt)}${row.original.lastSeenIp ? ` from ${row.original.lastSeenIp}` : ''}` : 'Never seen'}>
          <OnlineDot online={row.original.online} />{row.original.online ? 'Online' : `Seen ${fmtAgo(row.original.lastSeenAt)}`}
        </span>
      ) },
    { id: 'keyStorage', header: 'Key', accessorKey: 'keyStorage', enableSorting: true, size: 100,
      cell: ({ row }) => <span title={row.original.keyStorage === 'TPM' ? 'Private key held by the TPM chip' : 'Private key in protected software storage'}>{row.original.keyStorage}</span> },
    { id: 'machineName', header: 'Machine', accessorKey: 'machineName', enableSorting: true, size: 170,
      cell: ({ row }) => <span title={row.original.osDescription}>{row.original.machineName ?? '—'}</span> },
    { id: 'agentVersion', header: 'Version', accessorKey: 'agentVersion', enableSorting: true, size: 90, cell: ({ row }) => row.original.agentVersion ?? '—' },
    { id: 'printerCount', header: 'Printers', accessorKey: 'printerCount', enableSorting: true, size: 110,
      cell: ({ row }) => (
        <span>{row.original.printerCount}
          {row.original.offlinePrinterCount > 0 && <span style={{ color: 'var(--danger, #dc2626)', marginLeft: 6 }}>({row.original.offlinePrinterCount} offline)</span>}
        </span>
      ) },
  ], []);

  const getRowActions = useCallback((row: PrintAgent): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    const s = row.agentStatus;
    const act = (action: Action) => () => { setActionError(null); setActing({ agent: row, action }); };
    if (canManage && s === 'PENDING_APPROVAL') items.push({ id: 'approve', label: 'Approve', icon: <IconSystem.checkCircle size={16} />, onClick: act('approve'),
      description: 'Lets the agent take print jobs.' });
    if (canManage && s === 'SUSPENDED') items.push({ id: 'resume', label: 'Resume', icon: <IconSystem.refresh size={16} />, onClick: act('resume') });
    if (canManage && s === 'ACTIVE') items.push({ id: 'suspend', label: 'Suspend…', icon: <IconSystem.lock size={16} />, onClick: act('suspend'),
      description: 'Stops jobs going to it until resumed.' });
    if (canEdit && s !== 'REVOKED') items.push({ id: 'edit', label: 'Edit name / site', icon: <IconSystem.edit size={16} />, onClick: () => setEditing(row) });
    if (canAdd && s !== 'PENDING_APPROVAL' && !row.replacedByAgentId) items.push({ id: 'replace', label: 'Replace agent…', icon: <IconSystem.copy size={16} />,
      onClick: () => setPairing({ replace: row }), description: 'Pair a new machine that takes over its printers.' });
    if (canManage && s !== 'REVOKED') items.push({ id: 'revoke', label: 'Revoke…', icon: <IconSystem.trash size={16} />, onClick: act('revoke'), variant: 'danger',
      description: 'Final: the agent can never connect again.' });
    return items;
  }, [canManage, canEdit, canAdd]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const b: ActionButton[] = [];
    if (canAdd) b.push({ id: 'add', icon: <IconSystem.add size={18} />, label: '+', onClick: () => setPairing({ replace: null }), tooltip: 'Pair new agent', variant: 'icon' });
    b.push({ id: 'codes', icon: <IconSystem.lock size={18} />, label: '', onClick: () => setCodesOpen(true),
      tooltip: openCodes ? `Open pairing codes (${openCodes})` : 'Open pairing codes', variant: 'icon' });
    b.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: refresh, tooltip: 'Refresh data', variant: 'icon' });
    b.push({ id: 'export', icon: <IconSystem.download size={18} />, label: '', onClick: handleExport, tooltip: 'Export to CSV', variant: 'icon' });
    return b;
  }, [canAdd, openCodes, refresh, handleExport]);

  const statusOptions = (Object.keys(AGENT_STATUS_LABEL) as PrintAgentStatus[]).map((s) => ({ value: s, label: AGENT_STATUS_LABEL[s] }));
  const a = acting;
  const reasonCopy = a && {
    suspend: { title: `Suspend ${a.agent.agentName}?`, message: 'Its printers get no jobs until you resume it. Jobs already queued wait. A reason is required.', label: 'Suspend', danger: true },
    revoke: { title: `Revoke ${a.agent.agentName}?`, message: 'Final. The agent can never connect again; its printers stay and can move to another agent. To use this PC again, pair it as a new agent. A reason is required.', label: 'Revoke', danger: true },
    approve: null, resume: null,
  }[a.action];

  return (
    <div className="module-master-container" data-screen="print-agents">
      <ModuleHeader
        title="Print Agents"
        description={`The PCs that print for each site. Pair one with a one-time code${openCodes ? ` · ${openCodes} open pairing code${openCodes === 1 ? '' : 's'}` : ''}`}
        icon={<IconSystem.settings size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search agent, site or machine' }}
        filterConfig={{ onClick: () => { setDraftStatus(statusFilter); setFilterOpen(!filterOpen); }, isActive: filterOpen, tooltip: 'Toggle filters' }}
        actions={actionButtons}
      />
      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <SyutGrid data={rows} columns={columns} enableSorting enablePagination manualPagination totalCount={total}
              currentPage={(query.page ?? 1) - 1} pageSize={query.pageSize ?? 25} pageSizeOptions={[10, 25, 50, 100]}
              onPageChange={(i) => setQuery((q) => ({ ...q, page: i + 1 }))}
              onPageSizeChange={(s) => setQuery((q) => ({ ...q, pageSize: s, page: 1 }))}
              frozenRightColumns={['actions']} isLoading={loading} rowActions={getRowActions}
              emptyMessage={loadError ?? 'No Print Agents yet. Use + to pair the first one.'} />
          </div>
        </div>
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={16} /></button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect label="Status" options={statusOptions} value={draftStatus} placeholder="All statuses" size="sm" isClearable
                menuPortalTarget={document.body} onChange={(v) => setDraftStatus((v as string) || '')} />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setDraftStatus(''); setStatusFilter(''); }}>Clear</button>
            <button className="btn btn-primary" onClick={() => { setStatusFilter(draftStatus); setFilterOpen(false); }}>Apply</button>
          </div>
        </div>
      </div>

      <PairPrintAgentModal isOpen={!!pairing} replaceAgent={pairing?.replace} onClose={() => { setPairing(null); refresh(); }} onCreated={() => void loadCodes()} />
      <PairingCodesModal isOpen={codesOpen} canWithdraw={canWithdraw} onClose={() => setCodesOpen(false)} onChanged={() => void loadCodes()} />
      <PrintAgentEditModal agent={editing} onClose={() => setEditing(null)} onSaved={() => void load()} />

      <ConfirmDialog open={!!a && (a.action === 'approve' || a.action === 'resume')}
        title={a?.action === 'approve' ? `Approve ${a?.agent.agentName}?` : `Resume ${a?.agent.agentName ?? ''}?`}
        message={a?.action === 'approve'
          ? `${a?.agent.agentName} on ${a?.agent.machineName ?? 'its machine'} (${a?.agent.keyStorage} key) will take print jobs for its printers. Approve only a machine you recognise.${actionError ? `\n\n${actionError}` : ''}`
          : `Jobs go to its printers again.${actionError ? `\n\n${actionError}` : ''}`}
        confirmLabel={a?.action === 'approve' ? 'Approve' : 'Resume'} cancelLabel="Cancel" loading={busy} loadingMessage="Saving..."
        onConfirm={() => void runAction('')} onCancel={() => setActing(null)} />
      <PrintReasonModal isOpen={!!reasonCopy} title={reasonCopy?.title ?? ''} message={reasonCopy?.message ?? ''}
        confirmLabel={reasonCopy?.label ?? 'OK'} variant={reasonCopy?.danger ? 'danger' : 'default'} busy={busy} error={actionError}
        presets={a?.action === 'revoke' ? ['Machine replaced', 'Machine lost or stolen', 'Key may be compromised', 'No longer used'] : ['Maintenance', 'Unexpected behaviour', 'Under investigation']}
        confirmWord={a?.action === 'revoke' ? 'REVOKE' : undefined}
        onConfirm={(r) => void runAction(r)} onCancel={() => setActing(null)} />
    </div>
  );
};

export default PrintAgentsScreen;
